/**
 * novaLocomotion.ts
 * Ground/air acceleration and the jump state machine.
 *
 * Extracted from `physicsStep` so that file stays orchestration and keeps
 * clear of the AGENTS.md 300-line cap once crouch, wall slide and dash are
 * wired in. Behaviour is unchanged by the move.
 *
 * @module novaLocomotion
 */

import {
  JUMP_FORCE,
  AIR_JUMP_FORCE,
  JUMP_RELEASE_CLAMP,
  GROUND_FRICTION,
  GROUND_ACCELERATION,
  MAX_RUN_SPEED,
} from './physics';
import { JUMP_BUFFER_FRAMES } from '../../constants/gameConstants';
import type { NovaRuntime } from './gameStateTypes';

/** Directional input for one tick. */
export interface LocomotionInput {
  left: boolean;
  right: boolean;
  jumpPressed: boolean;
  jumpHeld: boolean;
}

/**
 * Apply horizontal acceleration, friction and the run-speed clamp.
 *
 * The crouch speed cap is deliberately not applied here — it runs after
 * collision resolution, where `isGrounded` is final for the tick.
 *
 * @param nova - Nova's runtime state; `vx` and `facingRight` are written.
 * @param input - Directional input for this tick.
 * @param dt - Fixed timestep in seconds.
 */
export function applyHorizontalInput(
  nova: NovaRuntime,
  input: LocomotionInput,
  dt: number,
): void {
  if (input.left && !input.right) {
    nova.vx -= GROUND_ACCELERATION * dt;
    nova.facingRight = false;
  } else if (input.right && !input.left) {
    nova.vx += GROUND_ACCELERATION * dt;
    nova.facingRight = true;
  } else if (nova.vx > 0) {
    nova.vx = Math.max(0, nova.vx - GROUND_FRICTION * dt);
  } else if (nova.vx < 0) {
    nova.vx = Math.min(0, nova.vx + GROUND_FRICTION * dt);
  }

  nova.vx = Math.max(-MAX_RUN_SPEED, Math.min(MAX_RUN_SPEED, nova.vx));
}

/**
 * Run the jump buffer, ground/air jump execution and variable jump height.
 *
 * @param nova - Nova's runtime state; jump and velocity fields are written.
 * @param input - Directional input for this tick.
 */
export function applyJump(nova: NovaRuntime, input: LocomotionInput): void {
  if (input.jumpPressed) {
    nova.jumpBufferFrames = JUMP_BUFFER_FRAMES;
  } else if (nova.jumpBufferFrames > 0) {
    nova.jumpBufferFrames--;
  }

  const canJump = nova.isGrounded || nova.coyoteFrames > 0;

  if (nova.jumpBufferFrames > 0 && canJump) {
    nova.vy = JUMP_FORCE;
    nova.isJumping = true;
    nova.isGrounded = false;
    nova.coyoteFrames = 0;
    nova.jumpBufferFrames = 0;
  } else if (
    nova.jumpBufferFrames > 0 &&
    !canJump &&
    nova.canDoubleJump === true &&
    (nova.airJumpsRemaining ?? 0) > 0
  ) {
    // DECISION: Mid-air double jump. `else if` so a single press can never burn both jumps.
    nova.vy = AIR_JUMP_FORCE;
    nova.isJumping = true;
    nova.airJumpsRemaining = (nova.airJumpsRemaining ?? 0) - 1;
    nova.jumpBufferFrames = 0;
  }

  // Variable-height jump: clamp upward velocity when the button is released early.
  if (!input.jumpHeld && nova.isJumping && nova.vy < JUMP_RELEASE_CLAMP) {
    nova.vy = JUMP_RELEASE_CLAMP;
  }
  if (nova.vy >= 0) {
    nova.isJumping = false;
  }
}
